import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, ArrowDown, ArrowUp, Calendar, Check, CheckCircle2, ChevronLeft, ChevronRight, FileSpreadsheet, Filter,
  History, Info, Lock, MapPin, MapPinCheck, RefreshCw, RotateCcw, Search, X,
} from 'lucide-react';
import { useSiteContext } from '../../SiteContext';
import { exportLaSick, getLaSick, laSickErrorMessage, LaSickJob, LaSickResponse, LaSickWeekRef } from '../../api/laSick';
import { ToggleSwitch } from '../../components/v2/adminManagement/AdminManagementKit';
import {
  ChipKey, ColumnFilters, DEFAULT_SORT, FilterCol, LA_SICK_FIRST_YEAR, LaRow, NO_POLICY, SCOPES, SORT_LABEL, ST_LABEL, ST_ORDER,
  Scope, SortKey, SortState, StKey,
  clearFilter, clearWeekFilters, columnCounts, deriveRow, effectiveSort, emptyFilters, exportFileName, pacificYear,
  exportSuffix, filterChips, fitJobChips, fmtDate, fmtRange, fmtSyncTime, hrs, inScope, isColumnFiltered, jobOptions,
  matchesFilters, matchesSearch, mdShort, money, nextSort, plural, policyOptions, population, scopeCounts, sortRows,
  weekBadge, weekOptions, weekTitle, weeksNewestFirst, yearSummary,
} from './laSickHelpers';
import { MdPopover, MdTh, MdTip, downloadBlob, useHideOnScroll, useMdToast, usePopoverDismiss } from './mdShared';
import './ModifiedDutyPage.css';
import './SickHoursPage.css';
import './LaSickPage.css';

/**
 * LA Sick — v2 page (People › LA Sick). NPM only (config.showLaSickPage + viewLASick).
 *
 * Built from docs/featureBriefs/design_handoff_la_sick_page_design (README is the spec).
 * Read-only: who did payroll-approved work on a Los Angeles job in the selected calendar
 * year, and whether Vista has them on CA LA SICK. Payroll adds the leave code in Vista; the
 * nightly sync moves the employee to the CA LA Sick policy.
 *
 * Driven by its own Year stepper (the shell hides the global week pill on this page). One
 * GET /admin/timeOff/laSick?year= per year; everything else is client-side. Export posts the
 * on-screen employee ids in display order to POST /admin/timeOff/laSick/export.
 *
 * NPM pay periods run Thursday – Wednesday: every week label is the API's weekNumber +
 * period startDate, never a computed Monday.
 *
 * Reuses the Modified Duty table / popovers / chips / search / export / toast (ModifiedDutyPage
 * .css + mdShared) and the Sick Hours summary strip, group header, amber pill and skeleton
 * (SickHoursPage.css). No layout shift: fixed 62px rows, job chips fold into +n (fitted on
 * render/resize only), fixed-height strip and chip row, reserved export / year widths, and the
 * detail panel is a fixed overlay that never resizes the table. The root is NOT a size
 * container (that would make it the containing block of the fixed panel and toast); the
 * container is the inner .la-body.
 */

interface ColDef {
  key: SortKey | 'go';
  label: string;
  full?: string;
  width: string;
  num?: boolean;
  filter?: FilterCol;
  /** Under the "LA work" group header. */
  grp?: boolean;
  /** Current year only. */
  cur?: boolean;
}

const COLS: ColDef[] = [
  { key: 'emp', label: 'Employee', width: '180px', filter: 'emp' },
  { key: 'st', label: 'Status', width: '156px', filter: 'st', cur: true },
  { key: 'hrs', label: 'Hours', full: 'LA hours', width: '84px', num: true, grp: true },
  { key: 'pc', label: 'Piece $', full: 'LA piece $', width: '96px', num: true, grp: true },
  { key: 'wks', label: 'Weeks', full: 'LA weeks', width: '88px', num: true, grp: true },
  { key: 'first', label: 'First', full: 'First LA week', width: '128px', filter: 'first', grp: true },
  { key: 'last', label: 'Last', full: 'Last LA week', width: '128px', filter: 'last', grp: true },
  { key: 'jobs', label: 'Jobs', full: 'LA jobs', width: '140px', filter: 'jobs', grp: true },
  { key: 'pol', label: 'Time-off policy', width: 'auto', filter: 'pol', cur: true },
  { key: 'go', label: '', width: '30px' },
];

const POP_TITLE: Record<FilterCol, string> = {
  emp: 'Employee', st: 'Status', first: 'First LA week', last: 'Last LA week', jobs: 'LA jobs', pol: 'Time-off policy',
};

const SKELETON_ROWS = 10;
const SKELETON_W: Partial<Record<SortKey, [number, number]>> = {
  emp: [62, 40], st: [58, 0], hrs: [30, 0], pc: [34, 0], wks: [24, 0], first: [48, 0], last: [48, 0], jobs: [60, 0], pol: [56, 44],
};

const SCOPE_EXPORT_HELP: Record<Scope, string> = {
  needs: 'Needs LA Sick', has: 'Has LA Sick', nowork: 'LA Sick, no LA work', all: 'All',
};

/* ====================================================================== *
 * Small pieces
 * ====================================================================== */

const StPill: React.FC<{ st: StKey; sm?: boolean }> = ({ st, sm }) => {
  if (st === 'needs') return <span className={`sh-need${sm ? ' sm' : ''}`}><AlertTriangle />{ST_LABEL.needs}</span>;
  if (st === 'has') return <span className={`la-pill ok${sm ? ' sm' : ''}`}><Check />{ST_LABEL.has}</span>;
  if (st === 'inact') return <span className={`la-pill neutral inact${sm ? ' sm' : ''}`}><History />{ST_LABEL.inact}</span>;
  return <span className={`la-pill neutral${sm ? ' sm' : ''}`}><Info />{ST_LABEL.nowork}</span>;
};

const WeekCell: React.FC<{ w: LaSickWeekRef | null; year: number }> = ({ w, year }) => {
  if (!w) return <span className="dash">—</span>;
  return (
    <span className="la-wk" title={weekTitle(w)}>
      <span className="la-wkn">{weekBadge(w, year)}</span>
      <span>{mdShort(w.startDate, year)}</span>
    </span>
  );
};

const JobChips: React.FC<{ jobs: LaSickJob[]; onTip: (el: HTMLElement | null) => void }> = ({ jobs, onTip }) => {
  const ref = useRef<HTMLDivElement>(null);
  const max = Math.min(2, jobs.length);
  const [shown, setShown] = useState(max);

  // Measure the (at most two) chips' natural widths — folded ones stay in the DOM, out of
  // flow — and fit on one line. Runs after render and on resize, never on hover or click.
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const chips = Array.from(el.querySelectorAll<HTMLElement>('.la-job'));
    const more = el.querySelector<HTMLElement>('.sh-more');
    const gap = parseFloat(window.getComputedStyle(el).columnGap) || 5;
    const next = fitJobChips(chips.map((c) => c.offsetWidth), more?.offsetWidth ?? 28, gap, el.clientWidth, 2);
    setShown((prev) => (prev === next ? prev : next));
  }, []);

  useLayoutEffect(() => { measure(); }, [measure, jobs]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    (document as any).fonts?.ready?.then(() => measure()).catch(() => undefined);
    return () => ro.disconnect();
  }, [measure]);

  if (!jobs.length) return <span className="dash">—</span>;
  const vis = Math.min(shown, max);
  const hidden = jobs.length - vis;
  return (
    <div
      ref={ref}
      className={`la-jobs${hidden > 0 ? ' over' : ''}`}
      onMouseEnter={(e) => onTip(e.currentTarget)}
      onMouseLeave={() => onTip(null)}
    >
      {jobs.slice(0, max).map((j, i) => (
        <span key={j.projectID} className={`la-job${i < vis ? '' : ' off'}`}>{j.altProjectID || `#${j.projectID}`}</span>
      ))}
      <span className={`sh-more${hidden > 0 ? '' : ' off'}`}>+{Math.max(1, hidden)}</span>
    </div>
  );
};

/* ====================================================================== *
 * Filter popover
 * ====================================================================== */

type Counts = ReturnType<typeof columnCounts>;

const LaFilterPopover: React.FC<{
  col: FilterCol;
  anchor: HTMLElement;
  filters: ColumnFilters;
  setFilters: React.Dispatch<React.SetStateAction<ColumnFilters>>;
  statusOptions: StKey[];
  stCounts: Counts['st'];
  scopeCounts: Counts;
  jobs: LaSickJob[];
  weeks: LaSickWeekRef[];
  policies: string[];
  year: number;
  onClose: () => void;
}> = ({ col, anchor, filters: f, setFilters, statusOptions, stCounts, scopeCounts: cnt, jobs, weeks, policies, year, onClose }) => {
  const set = <K extends keyof ColumnFilters>(k: K, v: ColumnFilters[K]) => setFilters((prev) => ({ ...prev, [k]: v }));

  function toggle<T extends string | number>(k: 'st' | 'jobs' | 'pol', order: T[], v: T, on: boolean) {
    setFilters((prev) => {
      const s = new Set<T>(prev[k] as T[]);
      if (on) s.add(v); else s.delete(v);
      return { ...prev, [k]: order.filter((x) => s.has(x)) };
    });
  }

  const text = (k: 'emp' | 'empId', ph: string, numeric = false) => (
    <input
      className="md-input"
      type="text"
      inputMode={numeric ? 'numeric' : undefined}
      value={f[k]}
      placeholder={ph}
      onChange={(e) => set(k, e.target.value)}
    />
  );

  const weekSelect = (k: 'firstFrom' | 'firstTo' | 'lastFrom' | 'lastTo') => (
    <select className="md-input la-select" value={f[k]} onChange={(e) => set(k, e.target.value)}>
      <option value="">Any</option>
      {weeks.map((w) => (
        <option key={w.payPeriodID} value={w.startDate}>{`${weekBadge(w, year)} · ${mdShort(w.startDate, year)}`}</option>
      ))}
    </select>
  );

  let body: React.ReactNode = null;
  if (col === 'emp') {
    body = (
      <>
        <label className="md-field"><span className="md-flab">Name contains</span>{text('emp', 'e.g. Garcia')}</label>
        <label className="md-field"><span className="md-flab">Employee ID contains</span>{text('empId', 'e.g. 104', true)}</label>
      </>
    );
  } else if (col === 'st') {
    body = (
      <>
        <div className="md-checks">
          {statusOptions.map((s) => (
            <label key={s} className="md-check">
              <input type="checkbox" checked={f.st.includes(s)} onChange={(e) => toggle('st', ST_ORDER, s, e.target.checked)} />
              <span className="md-check-l">{ST_LABEL[s]}</span>
              <span className="md-cc">{stCounts[s] ?? 0}</span>
            </label>
          ))}
        </div>
        <div className="md-phelp la-phelp">Counts ignore the quick filter.</div>
      </>
    );
  } else if (col === 'first' || col === 'last') {
    const a = col === 'first' ? 'firstFrom' : 'lastFrom';
    const b = col === 'first' ? 'firstTo' : 'lastTo';
    body = (
      <>
        <div className="md-prow">
          <label className="md-field"><span className="md-flab">From week</span>{weekSelect(a)}</label>
          <label className="md-field"><span className="md-flab">To week</span>{weekSelect(b)}</label>
        </div>
        <div className="md-phelp la-phelp">
          {col === 'first'
            ? 'Employees whose first LA week falls in this range.'
            : 'Employees whose most recent LA week falls in this range.'}
          {' '}Rows with no LA work don&apos;t match.
        </div>
      </>
    );
  } else if (col === 'jobs') {
    const order = jobs.map((j) => j.projectID);
    const listed = jobs.filter((j) => (cnt.jobs[j.projectID] ?? 0) > 0 || f.jobs.includes(j.projectID));
    body = listed.length ? (
      <div className="md-checks la-jchecks">
        {listed.map((j) => (
          <label key={j.projectID} className="md-check">
            <input
              type="checkbox"
              checked={f.jobs.includes(j.projectID)}
              onChange={(e) => toggle('jobs', order, j.projectID, e.target.checked)}
            />
            <span className="la-jc"><b className="mono">{j.altProjectID || `#${j.projectID}`}</b><span title={j.name}>{j.name}</span></span>
            <span className="md-cc">{cnt.jobs[j.projectID] ?? 0}</span>
          </label>
        ))}
      </div>
    ) : <div className="md-phelp la-phelp">No Los Angeles jobs in these rows.</div>;
  } else if (col === 'pol') {
    body = (
      <div className="md-checks">
        {policies.map((p) => (
          <label key={p} className="md-check">
            <input type="checkbox" checked={f.pol.includes(p)} onChange={(e) => toggle('pol', policies, p, e.target.checked)} />
            <span className={`md-check-l${p === NO_POLICY ? ' muted' : ''}`}>{p}</span>
            <span className="md-cc">{cnt.pol[p] ?? 0}</span>
          </label>
        ))}
      </div>
    );
  }

  return (
    <MdPopover
      anchor={anchor}
      title={POP_TITLE[col]}
      wide={col !== 'emp' && col !== 'st'}
      focusKey={col}
      canClear={isColumnFiltered(f, col)}
      onClear={() => setFilters((prev) => clearFilter(prev, col))}
      onClose={onClose}
    >
      {body}
    </MdPopover>
  );
};

/* ====================================================================== *
 * The page
 * ====================================================================== */

const LaSickPage: React.FC = () => {
  const { createPopup } = useSiteContext();
  // The tenant's (Pacific) year, as the API uses — a browser already in the new year on Dec 31
  // evening would otherwise ask for a year the API rejects.
  const currentYear = useMemo(() => pacificYear(), []);

  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState<LaSickResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [scope, setScope] = useState<Scope>('needs');
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<ColumnFilters>(emptyFilters);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [showInactive, setShowInactive] = useState(false);
  // Turning Show inactive off also drops a Needed (inactive) status filter, whose option disappears.
  useEffect(() => {
    if (!showInactive) setFilters((f) => (f.st.includes('inact') ? { ...f, st: f.st.filter((x) => x !== 'inact') } : f));
  }, [showInactive]);
  const [pop, setPop] = useState<{ col: FilterCol; anchor: HTMLElement } | null>(null);
  const [tip, setTip] = useState<{ row: LaRow; rect: DOMRect } | null>(null);
  const [selID, setSelID] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  const { toast, toastOn, showToast } = useMdToast();

  const scrollRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  /* ---------------- data ---------------- */

  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setLoadError(null);
    setPop(null);
    setTip(null);
    setSelID(null);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    try {
      const payload = await getLaSick(year);
      if (seq === loadSeq.current) setData(payload);
    } catch (err) {
      if (seq === loadSeq.current) {
        setData(null);
        setLoadError(laSickErrorMessage(err));
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  /* ---------------- derived ---------------- */

  const fresh = !!data && data.year === year;
  const busy = loading || (!fresh && !loadError);
  const isCur = fresh && data ? data.isCurrentYear : year === currentYear;
  const effScope: Scope = isCur ? scope : 'all';

  const allRows = useMemo(() => (fresh && data ? data.rows.map(deriveRow) : []), [data, fresh]);
  const popRows = useMemo(() => population(allRows, showInactive), [allRows, showInactive]);
  const matched = useMemo(
    () => popRows.filter((r) => matchesSearch(r, query) && matchesFilters(r, filters, isCur)),
    [popRows, query, filters, isCur],
  );
  const counts = useMemo(() => scopeCounts(matched), [matched]);
  const scopeRows = useMemo(() => popRows.filter((r) => inScope(r, effScope, isCur)), [popRows, effScope, isCur]);
  const rows = useMemo(
    () => sortRows(matched.filter((r) => inScope(r, effScope, isCur)), sort, isCur),
    [matched, effScope, isCur, sort],
  );
  const jobs = useMemo(() => jobOptions(allRows), [allRows]);
  const jobByID = useMemo(() => new Map(jobs.map((j) => [j.projectID, j])), [jobs]);
  const weeks = useMemo(() => weekOptions(allRows), [allRows]);
  const weekByStart = useMemo(() => new Map(weeks.map((w) => [w.startDate, w])), [weeks]);
  const policies = useMemo(() => policyOptions(allRows), [allRows]);
  const popCounts = useMemo(() => columnCounts(scopeRows), [scopeRows]);
  const stCounts = useMemo(() => columnCounts(popRows).st, [popRows]);
  const summary = useMemo(() => (fresh && data ? yearSummary(data, allRows, showInactive) : null), [fresh, data, allRows, showInactive]);
  const neededInactiveTotal = useMemo(() => allRows.filter((r) => r.st === 'inact').length, [allRows]);
  const chips = useMemo(
    () => filterChips(
      filters,
      isCur,
      (start) => { const w = weekByStart.get(start); return w ? weekBadge(w, year) : mdShort(start, year); },
      (id) => jobByID.get(id)?.altProjectID || `#${id}`,
    ),
    [filters, isCur, weekByStart, jobByID, year],
  );

  const cols = isCur ? COLS : COLS.filter((c) => !c.cur).map((c) => (c.key === 'jobs' ? { ...c, width: 'auto' } : c));
  const statusOptions: StKey[] = showInactive ? ST_ORDER : ST_ORDER.filter((s) => s !== 'inact');

  const sel = selID != null ? popRows.find((r) => r.id === selID) ?? null : null;
  // The panel keeps showing the last employee while it slides out (it is hidden, not unmounted).
  const lastSel = useRef<LaRow | null>(null);
  if (sel) lastSel.current = sel;
  const selIdx = sel ? rows.findIndex((r) => r.id === sel.id) : -1;

  /* ---------------- popover / tooltip ---------------- */

  const closePop = useCallback(() => setPop(null), []);
  usePopoverDismiss(!!pop, closePop);

  const togglePop = (col: FilterCol, btn: HTMLButtonElement) => {
    const th = btn.closest('th') as HTMLElement | null;
    setPop((p) => (p?.col === col ? null : { col, anchor: th ?? btn }));
  };

  const hideTip = useCallback(() => setTip(null), []);
  useHideOnScroll(!!tip, hideTip);

  /* ---------------- detail panel ---------------- */

  const scrollRowIntoView = (id: number) => {
    const sc = scrollRef.current;
    const tr = sc?.querySelector<HTMLTableRowElement>(`tr[data-id="${id}"]`);
    if (!sc || !tr) return;
    const headH = sc.querySelector('thead')?.getBoundingClientRect().height ?? 0;
    const top = tr.offsetTop - headH;
    const bottom = tr.offsetTop + tr.offsetHeight - sc.clientHeight;
    if (sc.scrollTop > top) sc.scrollTop = top;
    else if (sc.scrollTop < bottom) sc.scrollTop = bottom;
  };

  const step = useCallback((d: number) => {
    if (selIdx < 0) return;
    const next = rows[selIdx + d];
    if (!next) return;
    setSelID(next.id);
    scrollRowIntoView(next.id);
  }, [rows, selIdx]);

  useEffect(() => {
    if (selID == null) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (pop) return; // the popover's own Escape closes it first
      // Typing in search / a field: Esc and arrows belong to that field, not the panel.
      const t = document.activeElement?.tagName ?? '';
      if (/INPUT|SELECT|TEXTAREA/.test(t)) return;
      if (e.key === 'Escape') { setSelID(null); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      step(e.key === 'ArrowDown' ? 1 : -1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selID, pop, step]);

  // A year change / reload drops the selection; so does the row leaving the population.
  useEffect(() => { if (selID != null && !sel && !busy) setSelID(null); }, [selID, sel, busy]);

  /* ---------------- actions ---------------- */

  const changeScope = (s: Scope) => {
    setScope(s);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const changeYear = (y: number) => {
    if (y === year || y < LA_SICK_FIRST_YEAR || y > currentYear) return;
    // Search, quick filter, sort and non-week filters are kept; week numbers differ between years.
    setFilters((f) => clearWeekFilters(f));
    setYear(y);
  };

  const clearAll = (withSearch: boolean) => {
    setFilters(emptyFilters());
    if (withSearch) setQuery('');
  };

  const removeChip = (key: ChipKey) => setFilters((f) => clearFilter(f, key));

  const toggleRow = (id: number) => setSelID((cur) => (cur === id ? null : id));

  const doExport = async () => {
    if (!rows.length || exporting || busy || !data) return;
    setExporting(true);
    try {
      const { blob, fileName, employeeRows: n, detailRows: lines } = await exportLaSick(
        { year: data.year, employeeIDs: rows.map((r) => r.id), suffix: exportSuffix(effScope, isCur) },
        exportFileName(data, effScope),
      );
      downloadBlob(blob, fileName);
      showToast(<><CheckCircle2 /><span><b>{fileName}</b> downloaded · Employees {n} · Weekly detail {lines}</span></>);
    } catch (err: any) {
      createPopup('Export failed', err?.message || 'Could not export LA Sick.');
    } finally {
      setExporting(false);
    }
  };

  const onTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
    e.currentTarget.classList.toggle('scrolled-x', e.currentTarget.scrollLeft > 0);
  };

  /* ---------------- render pieces ---------------- */

  const eff = effectiveSort(sort, isCur);

  const th = (c: ColDef, rowSpan?: number) => {
    const key = c.key as SortKey;
    const filterCol = c.filter;
    return (
      <MdTh
        key={key}
        label={c.label}
        fullLabel={c.full}
        num={c.num}
        rowSpan={rowSpan}
        className={c.grp ? 'sh-sub' : undefined}
        sorted={eff.key === key}
        dir={eff.dir}
        filtered={filterCol ? isColumnFiltered(filters, filterCol) && (isCur || (filterCol !== 'st' && filterCol !== 'pol')) : false}
        popOpen={!!filterCol && pop?.col === filterCol}
        onSort={() => setSort((s) => nextSort(effectiveSort(s, isCur), key))}
        onFilter={filterCol ? (btn) => togglePop(filterCol, btn) : undefined}
      />
    );
  };

  const groupLabel = `LA work · ${year}${isCur ? ' YTD' : ''}`;
  const headRow1: React.ReactNode[] = [];
  const headRow2: React.ReactNode[] = [];
  cols.forEach((c) => {
    if (c.key === 'go') {
      headRow1.push(<th key="go" rowSpan={2} className="sh-th-go"><span className="md-sr">Open detail</span></th>);
    } else if (c.grp) {
      if (c.key === 'hrs') headRow1.push(<th key="grp" colSpan={6} className="sh-grp"><span>{groupLabel}</span></th>);
      headRow2.push(th(c));
    } else {
      headRow1.push(th(c, 2));
    }
  });

  const policyCell = (x: LaRow) => {
    const p = x.policy;
    if (!p) return <div className="md-cell2 la-pol"><span className="nm la-polnone">{NO_POLICY}</span></div>;
    const codes = p.leaveCodes.length ? p.leaveCodes.join(', ') : p.assignedByAdmin ? 'Assigned in the Admin App' : 'Synced from Vista · codes not recorded';
    if (!x.active) {
      const asOf = p.asOf ? mdShort(p.asOf, year) : '';
      const title = `As of the last Vista sync while active${p.asOf ? ` (${fmtDate(p.asOf)})` : ''}.${x.terminatedOn ? ` Terminated ${fmtDate(x.terminatedOn)}.` : ''}`;
      return (
        <div className="md-cell2 la-pol">
          <span className={`nm${p.isLaSick ? ' la-polla' : ''}`} title={p.name}>{p.name}</span>
          <span className="sub la-asof" title={title}>
            <History />
            <span className="mono">{codes}</span>
            <span>{asOf ? ` · as of ${asOf}` : ' · as of last sync while active'}</span>
          </span>
        </div>
      );
    }
    const synced = fmtSyncTime(data?.policiesSyncedAt);
    return (
      <div className="md-cell2 la-pol">
        <span className={`nm${p.isLaSick ? ' la-polla' : ''}`} title={p.name}>{p.name}</span>
        <span className="sub mono la-codes" title={synced ? `Vista leave codes, last sync ${synced}` : 'Vista leave codes'}>{codes}</span>
      </div>
    );
  };

  const onJobTip = (x: LaRow) => (el: HTMLElement | null) => {
    setTip(el ? { row: x, rect: el.getBoundingClientRect() } : null);
  };

  const row = (x: LaRow) => {
    const isSel = x.id === selID;
    return (
      <tr
        key={x.id}
        data-id={x.id}
        className={`sh-row la-row${x.active ? '' : ' ended'}${isSel ? ' la-sel' : ''}`}
        tabIndex={0}
        aria-selected={isSel}
        onClick={() => toggleRow(x.id)}
        onKeyDown={(e) => { if (e.key === 'Enter') toggleRow(x.id); }}
      >
        <td>
          <div className="md-emp">
            <span className="nm" title={x.name}>{x.name || '—'}</span>
            <span className="sub la-idl">
              <span className="mono">#{x.empID}</span>
              {!x.active && (
                <span className="la-inact" title={x.terminatedOn ? `Terminated ${fmtDate(x.terminatedOn)}` : 'Inactive'}>Inactive</span>
              )}
            </span>
          </div>
        </td>
        {isCur && (
          <td>
            <div className="md-cell2">
              {x.st ? <StPill st={x.st} /> : <span className="dash">—</span>}
              {x.isNew && x.first && (
                <span
                  className="sub la-new"
                  title={`First Los Angeles work is in week ${weekBadge(x.first, year)}, one of the latest two approved weeks`}
                >
                  <b>New</b>first LA week
                </span>
              )}
            </div>
          </td>
        )}
        <td className="num">{x.hrs ? <span className="mono">{hrs(x.hrs)}</span> : <span className="dash">—</span>}</td>
        <td className="num">{x.pc ? <span className="mono">{money(x.pc)}</span> : <span className="dash">—</span>}</td>
        <td className="num">{x.wks ? <span className="mono">{x.wks}</span> : <span className="dash">—</span>}</td>
        <td><WeekCell w={x.first} year={year} /></td>
        <td><WeekCell w={x.last} year={year} /></td>
        <td><JobChips jobs={x.jobs} onTip={onJobTip(x)} /></td>
        {isCur && <td>{policyCell(x)}</td>}
        <td className="sh-go"><ChevronRight /></td>
      </tr>
    );
  };

  const skeleton = () => Array.from({ length: SKELETON_ROWS }, (_, i) => (
    <tr key={`sk${i}`} className="sh-skrow">
      {cols.filter((c) => c.key !== 'go').map((c, j) => {
        const [w, sub] = SKELETON_W[c.key as SortKey] ?? [40, 0];
        return (
          <td key={c.key} className={c.num ? 'num' : undefined}>
            <span className="sh-sk" style={{ width: `${w + ((i * 7 + j * 13) % 20)}%` }} />
            {sub > 0 && <span className="sh-sk sm" style={{ width: `${sub}%` }} />}
          </td>
        );
      })}
      <td />
    </tr>
  ));

  const thru = fresh && data?.throughWeek ? data.throughWeek : null;

  const emptyBody = () => {
    const filteredOut = chips.length > 0 || !!query.trim();
    let content: React.ReactNode;
    if (loadError) {
      content = (
        <div className="md-empty">
          <div className="eic la-eic-err"><AlertTriangle /></div>
          <div className="et">Couldn&apos;t load LA Sick for {year}</div>
          <p>{loadError} Nothing has changed in Vista or on any timesheet.</p>
          <button type="button" className="md-btn sm" onClick={() => load()}><RotateCcw />Try again</button>
        </div>
      );
    } else if (!popRows.length) {
      content = (
        <div className="md-empty">
          <div className="eic"><MapPin /></div>
          <div className="et">No Los Angeles work found in {year}</div>
          <p>
            Nobody has payroll-approved work on a Vista job in the city of Los Angeles
            {isCur ? (thru ? ` through week #${thru.weekNumber}` : ' yet this year') : ` in ${year}`}
            {isCur ? ', and nobody is on an LA Sick policy' : ''}.
          </p>
        </div>
      );
    } else if (filteredOut) {
      const where = effScope === 'all' ? String(year) : SCOPE_EXPORT_HELP[effScope];
      content = (
        <div className="md-empty">
          <div className="eic"><Filter /></div>
          <div className="et">No employees match these filters</div>
          <p>
            Nothing in {where} matches{query.trim() ? ` “${query.trim()}”` : ''}
            {chips.length ? ` with ${plural(chips.length, 'column filter')}` : ''}.
          </p>
          <button type="button" className="md-btn sm" onClick={() => clearAll(true)}><X />Clear filters</button>
        </div>
      );
    } else if (effScope === 'needs') {
      content = (
        <div className="md-empty">
          <div className="eic"><CheckCircle2 /></div>
          <div className="et">Everyone with LA work has LA Sick</div>
          <p>Employees who worked a Los Angeles job in {year} without the CA LA SICK code in Vista show up here.</p>
          <button type="button" className="md-btn sm" onClick={() => changeScope('all')}>View all employees</button>
        </div>
      );
    } else {
      content = (
        <div className="md-empty">
          <div className="eic"><CheckCircle2 /></div>
          <div className="et">No one in {SCOPE_EXPORT_HELP[effScope]}</div>
          <p>
            {effScope === 'has'
              ? 'Nobody with LA work is on an LA Sick policy yet.'
              : `Everyone on LA Sick has Los Angeles work in ${year}.`}
          </p>
          <button type="button" className="md-btn sm" onClick={() => changeScope('all')}>View all employees</button>
        </div>
      );
    }
    return <tr className="md-empty-row"><td colSpan={cols.length}>{content}</td></tr>;
  };

  const n = rows.length;
  const scopeTotal = scopeRows.length;
  const sk = <span className="sh-sk w48" />;
  const dead = busy || !!loadError;

  let body: React.ReactNode;
  if (busy) body = skeleton();
  else if (n) body = rows.map(row);
  else body = emptyBody();

  let sortedBy: React.ReactNode;
  const lbl = SORT_LABEL[eff.key];
  const dirWord = eff.dir === 'asc' ? 'ascending' : 'descending';
  if (!isCur) {
    sortedBy = eff.key === 'last' && eff.dir === 'desc'
      ? <>Sorted by <b>Last LA week</b> newest, then <b>Employee</b></>
      : <>Sorted by <b>{lbl}</b> {dirWord}, then Last LA week</>;
  } else {
    sortedBy = eff.key === 'st' && eff.dir === 'asc'
      ? <>Sorted by <b>Status</b> (Needs first), then <b>Last LA week</b> newest, then <b>Employee</b></>
      : <>Sorted by <b>{lbl}</b> {dirWord}, then Status</>;
  }

  /* ---------------- header meta line ---------------- */

  let meta: React.ReactNode;
  if (busy) {
    meta = <span><RefreshCw />Loading {year}…</span>;
  } else if (loadError || !data) {
    meta = <span><Info />{year}</span>;
  } else if (isCur) {
    const synced = fmtSyncTime(data.policiesSyncedAt);
    meta = (
      <>
        <span>
          <CheckCircle2 />
          {thru
            ? <>Payroll-approved work through week <b>#{thru.weekNumber}</b> ({fmtRange(thru.startDate, thru.endDate, true)})</>
            : <>No payroll-approved weeks yet in {year}</>}
        </span>
        {synced && (
          <>
            <span className="dot">·</span>
            <span><RefreshCw />Vista policies as of last sync {synced}</span>
          </>
        )}
      </>
    );
  } else {
    const yw = data.yearWeeks;
    meta = (
      <>
        <span>
          <CheckCircle2 />
          Payroll-approved Los Angeles work for all of <b>{year}</b>
          {yw ? <> (weeks {weekBadge(yw.firstWeek, year)} – {weekBadge(yw.lastWeek, year)})</> : null}
        </span>
        <span className="dot">·</span>
        <span><Info />Shown for reference only. LA Sick codes reset each year, so past years have no status.</span>
      </>
    );
  }

  /* ---------------- summary strip ---------------- */

  const tileVal = (v: React.ReactNode) => (busy ? sk : loadError ? <span className="dash">—</span> : v);
  const tile = (k: 'needs' | 'has' | 'nowork', label: string) => {
    const c = summary ? summary[k] : 0;
    const attn = k === 'needs' && c > 0 && !dead;
    return (
      <button
        type="button"
        className={`sh-stat sh-stat-btn la-st-${k}${attn ? ' attn' : ''}${k === 'has' && c > 0 && !dead ? ' ok' : ''}`}
        onClick={() => changeScope(k)}
        disabled={dead || !c || effScope === k}
        title={`Show ${ST_LABEL[k]}`}
      >
        <span className="sl">{label}</span>
        <span className="sv">{tileVal(k === 'needs' && c ? <><AlertTriangle />{c}</> : c)}</span>
      </button>
    );
  };
  const laHours = summary ? hrs(summary.laHours) : '0.0';
  const popWord = showInactive ? 'active + inactive' : 'active employees';
  let caption: string;
  if (busy) caption = 'Loading year…';
  else if (loadError) caption = "Couldn't load";
  else if (!isCur) caption = `${year} · reference only · ${popWord}`;
  else {
    caption = `${year} YTD · ${popWord}`;
    if (showInactive && neededInactiveTotal) caption += ` · ${neededInactiveTotal} needed (inactive), not in Needs`;
  }

  /* ---------------- detail panel ---------------- */

  const panel = () => {
    const x = sel ?? lastSel.current;
    if (!x) return null;
    let note: React.ReactNode;
    const thruTxt = thru ? ` through week #${thru.weekNumber}` : '';
    if (!isCur) {
      note = <div className="la-note"><Info /><span>{year} Los Angeles work, shown for reference only. LA Sick codes reset each year, so past years have no LA Sick status.</span></div>;
    } else if (x.st === 'inact') {
      note = (
        <div className="la-note">
          <History />
          <span>
            {x.terminatedOn ? `Terminated ${fmtDate(x.terminatedOn)}. ` : 'Inactive. '}
            Had Los Angeles work in {year} without LA Sick. Not counted toward Needs LA Sick.
          </span>
        </div>
      );
    } else if (x.st === 'needs') {
      note = (
        <div className="la-note warn">
          <AlertTriangle />
          <span>
            Has Los Angeles work in {year} and isn&apos;t on LA Sick. Add the <b className="mono">CA LA SICK</b> leave code in
            Vista; the nightly sync moves them to <b>CA LA Sick</b>.
          </span>
        </div>
      );
    } else if (x.st === 'nowork') {
      note = <div className="la-note"><Info /><span>On LA Sick with no Los Angeles work in {year}{thruTxt}. Review in Vista.</span></div>;
    } else {
      note = <div className="la-note ok"><CheckCircle2 /><span>On LA Sick. Nothing to do.</span></div>;
    }

    const p = x.policy;
    const synced = fmtSyncTime(data?.policiesSyncedAt);
    const groups = weeksNewestFirst(x);

    return (
      <>
        <div className="la-dh">
          <div className="la-dh-t">
            <div className="la-dh-n" title={x.name}>{x.name || '—'}</div>
            <div className="la-dh-s">
              <span className="mono">#{x.empID}</span>
              {x.active
                ? <span>NPM · CA</span>
                : <><span className="la-inact">Inactive</span><span>{x.terminatedOn ? `Terminated ${fmtDate(x.terminatedOn)}` : 'Inactive in Vista'}</span></>}
            </div>
          </div>
          <div className="la-dh-a">
            <button type="button" className="la-iconbtn" onClick={() => step(-1)} disabled={selIdx <= 0} title="Previous employee (↑)" aria-label="Previous employee"><ArrowUp /></button>
            <button type="button" className="la-iconbtn" onClick={() => step(1)} disabled={selIdx < 0 || selIdx >= rows.length - 1} title="Next employee (↓)" aria-label="Next employee"><ArrowDown /></button>
            <button type="button" className="la-iconbtn" onClick={() => setSelID(null)} title="Close (Esc)" aria-label="Close"><X /></button>
          </div>
        </div>
        <div className="la-db">
          <div className="la-drow">
            {isCur && x.st ? <StPill st={x.st} /> : <span className="la-segnote"><Info />Reference only</span>}
            {isCur && x.isNew && x.first && <span className="la-new"><b>New</b>first LA work in week {weekBadge(x.first, year)}</span>}
            <span className="grow" />
            <span className="la-dpos">{selIdx >= 0 ? `${selIdx + 1} of ${rows.length}` : 'Not in current filter'}</span>
          </div>
          {note}
          <div className="la-dstats">
            <div><span className="sl">LA hours</span><span className="sv">{x.hrs ? hrs(x.hrs) : '—'}</span></div>
            <div><span className="sl">LA piece $</span><span className="sv">{x.pc ? money(x.pc) : '—'}</span></div>
            <div><span className="sl">LA weeks</span><span className="sv">{x.wks || '—'}</span></div>
            <div><span className="sl">LA jobs</span><span className="sv">{x.jobs.length || '—'}</span></div>
          </div>
          {isCur && (
            <div className="la-dpol">
              <span className="sl">Time-off policy</span>
              <span className={`la-dpol-n${p ? (p.isLaSick ? ' la-polla' : '') : ' la-polnone'}`}>{p ? p.name : NO_POLICY}</span>
              <span className="la-dpol-c">
                {p && (
                  <span className="mono">
                    {p.leaveCodes.length ? p.leaveCodes.join(', ') : p.assignedByAdmin ? 'Assigned in the Admin App' : 'Synced from Vista · codes not recorded'}
                  </span>
                )}
                <span>
                  {!x.active
                    ? `as of last sync while active${p?.asOf ? ` · ${fmtDate(p.asOf)}` : ''}`
                    : synced ? `Vista · last sync ${synced}` : 'Vista'}
                </span>
              </span>
            </div>
          )}
          {x.wks > 0 ? (
            <>
              <div className="la-dsec">
                <div className="la-dsec-h">LA jobs <span>{x.jobs.length}</span></div>
                <div className="la-jlist">
                  {x.jobs.map((j) => (
                    <div key={j.projectID} className="la-jl">
                      <span className="la-job">{j.altProjectID || `#${j.projectID}`}</span>
                      <span className="la-jl-n">
                        <b title={j.name}>{j.name || '—'}</b>
                        <span title={[j.address, j.city, j.state, j.zip].filter(Boolean).join(', ')}>
                          {j.address ? `${j.address} · ` : ''}<b className="la-city">{j.city || '—'}</b>{j.zip ? ` ${j.zip}` : ''}
                        </span>
                      </span>
                      <span className="la-jl-v">
                        <b className="mono">{j.hours ? `${hrs(j.hours)} h` : '—'}</b>
                        <span>{plural(j.weekCount, 'wk')}{j.pieceAmount ? ` · ${money(j.pieceAmount)}` : ''}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="la-dsec">
                <div className="la-dsec-h">By week <span>{groups.length}</span><em>newest first</em></div>
                <div className="la-wl-h"><span>Job</span><span>Job city</span><span className="r">Hours</span><span className="r">Piece $</span></div>
                {groups.map((g) => {
                  const cross = g.week.accountingYear !== year || g.week.startDate.slice(0, 4) !== String(year);
                  return (
                    <div key={g.week.payPeriodID} className="la-wg">
                      <div className="la-wg-h" title={weekTitle(g.week)}>
                        <span className="la-wkn">{weekBadge(g.week, year)}</span>
                        <span>{fmtRange(g.week.startDate, g.week.endDate, cross)}</span>
                        <span className="grow" />
                        <span className="mono">
                          {g.hours ? `${hrs(g.hours)} h` : ''}{g.hours && g.piece ? ' · ' : ''}{g.piece ? money(g.piece) : ''}
                        </span>
                      </div>
                      {g.lines.map((l) => (
                        <div key={`${l.projectID}|${l.claimType ?? ''}`} className="la-wl">
                          <span className="la-wl-j">
                            <span className="mono">{l.job?.altProjectID || `#${l.projectID}`}</span>
                            <span title={l.job?.name}>{l.job?.name ?? ''}</span>
                          </span>
                          <span className="la-wl-c" title="Vista job city">{l.job?.city || '—'}</span>
                          <span className="r mono">{l.hours ? hrs(l.hours) : '—'}</span>
                          <span className="r mono">
                            {l.pieceAmount
                              ? <><i title={l.claimType === 'RED' ? 'Misc (red) task claim' : 'Piece / task claim'}>{l.claimType === 'RED' ? 'red' : 'pc'}</i>{money(l.pieceAmount)}</>
                              : '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="la-dsec">
              <div className="md-empty la-dempty">
                <div className="eic"><MapPin /></div>
                <div className="et">No Los Angeles work in {year}</div>
                <p>No payroll-approved hours or claims on a Los Angeles job{isCur ? thruTxt : ''}.</p>
              </div>
            </div>
          )}
        </div>
        <div className="la-df"><Lock />Read-only. LA Sick is added in Vista, not here.</div>
      </>
    );
  };

  return (
    <div className="md-page la-page">
      <div className="la-body">
        <div className="md-head la-head">
          <div className="ph-ic"><MapPinCheck /></div>
          <div className="grow">
            <h1>LA Sick</h1>
            <div className="ph-sub">
              Employees who worked on a Los Angeles job this year, and whether they&apos;re on LA Sick. Payroll adds the
              CA LA SICK leave code in Vista; this page is read-only.
            </div>
            <div className="la-meta">{meta}</div>
          </div>
          <div className="ph-actions">
            <div className="la-year" role="group" aria-label="Year">
              <button
                type="button"
                className="la-yarrow"
                onClick={() => changeYear(year - 1)}
                disabled={year <= LA_SICK_FIRST_YEAR}
                title="Previous year"
                aria-label="Previous year"
              >
                <ChevronLeft />
              </button>
              <span className="la-yface">
                <Calendar />
                <b>{year}</b>
                <span className="la-ycur" style={{ visibility: year === currentYear ? 'visible' : 'hidden' }}>YTD</span>
              </span>
              <button
                type="button"
                className="la-yarrow"
                onClick={() => changeYear(year + 1)}
                disabled={year >= currentYear}
                title="Next year"
                aria-label="Next year"
              >
                <ChevronRight />
              </button>
            </div>
            <button type="button" className="md-btn md-export la-export" onClick={doExport} disabled={dead || !n || exporting}>
              {exporting
                ? <><span className="md-spin" /><span>Exporting…</span></>
                : <><FileSpreadsheet /><span>{dead ? 'Export to Excel' : `Export ${plural(n, 'row')}`}</span></>}
            </button>
          </div>
        </div>

        <div className="sh-sum la-sum">
          <div className="sh-stat"><span className="sl">With LA work</span><span className="sv">{tileVal(summary?.withWork ?? 0)}</span></div>
          {isCur && (
            <>
              {tile('needs', 'Needs LA Sick')}
              {tile('has', 'Has LA Sick')}
              {tile('nowork', 'LA Sick, no work')}
            </>
          )}
          <div className="sh-stat"><span className="sl">LA hours {isCur ? 'YTD' : year}</span><span className="sv">{tileVal(laHours)}</span></div>
          <div className="sh-sum-cap" title={caption}>{caption}</div>
        </div>

        <div className="md-card">
          <div className="md-bar">
            {isCur ? (
              <div className="md-seg" role="radiogroup" aria-label="Quick filter">
                {SCOPES.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    role="radio"
                    aria-checked={scope === s.key}
                    className={scope === s.key ? 'on' : ''}
                    onClick={() => changeScope(s.key)}
                  >
                    {s.label}
                    <span className={`md-cnt${s.key === 'needs' && counts.needs > 0 && !dead ? ' sh-cnt-warn' : ''}`}>
                      {dead ? '–' : counts[s.key]}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="md-seg la-seg-off">
                <span className="la-segnote"><Info />{year} · reference only, no LA Sick status</span>
              </div>
            )}
            <div className="md-search">
              <Search className="md-search-ic" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                placeholder="Search name, ID or job number…"
                aria-label="Search"
                onChange={(e) => setQuery(e.target.value)}
              />
              <button
                type="button"
                className="md-qx"
                aria-label="Clear search"
                style={{ visibility: query ? 'visible' : 'hidden' }}
                onClick={() => { setQuery(''); searchRef.current?.focus(); }}
              >
                <X />
              </button>
            </div>
            <div className="la-inact-t">
              <ToggleSwitch checked={showInactive} onChange={setShowInactive} ariaLabel="Show inactive employees" />
              {/* Text click toggles too (ToggleSwitch is its own <label>, so no wrapping label). */}
              <span className="la-inact-l" onClick={() => setShowInactive((v) => !v)} aria-hidden="true">Show inactive</span>
            </div>
          </div>

          <div className="md-chips">
            <div className="md-chips-l">
              {chips.length
                ? (
                  <>
                    {chips.map((c) => (
                      <span key={c.key} className="md-chip" title={c.title}>
                        <b>{c.label}</b>{c.value}
                        <button type="button" aria-label={`Remove ${c.label} filter`} onClick={() => removeChip(c.key)}><X /></button>
                      </span>
                    ))}
                    <button type="button" className="md-linkbtn md-clearall" onClick={() => clearAll(false)}>Clear all</button>
                  </>
                )
                : <span className="md-hint-row"><Filter />Filter any column from the icon in its header.</span>}
            </div>
            <div className="md-shown" aria-live="polite">
              {dead ? ' ' : n === scopeTotal ? plural(n, 'employee') : <><b>{n}</b> of {scopeTotal}</>}
            </div>
          </div>

          <div className="md-scroll sh-scroll" ref={scrollRef} onScroll={onTableScroll}>
            <table className={`md-tbl sh-tbl la-tbl${isCur ? '' : ' la-past'}`}>
              <colgroup>{cols.map((c) => <col key={c.key} style={{ width: c.width }} />)}</colgroup>
              <thead>
                <tr className="sh-r1">{headRow1}</tr>
                <tr className="sh-r2">{headRow2}</tr>
              </thead>
              <tbody>{body}</tbody>
            </table>
          </div>

          <div className="md-foot">
            <span>{sortedBy}</span>
            <span className="grow" />
            <span className="md-legend la-legend"><MapPin />LA job = Vista job city is exactly “Los Angeles”</span>
          </div>
        </div>
      </div>

      <aside className={`la-dr${sel ? ' on' : ''}`} role="dialog" aria-label="LA work detail" aria-hidden={!sel}>
        {busy && !sel ? null : panel()}
      </aside>

      {pop && (
        <LaFilterPopover
          col={pop.col}
          anchor={pop.anchor}
          filters={filters}
          setFilters={setFilters}
          statusOptions={statusOptions}
          stCounts={stCounts}
          scopeCounts={popCounts}
          jobs={jobs}
          weeks={weeks}
          policies={policies}
          year={year}
          onClose={closePop}
        />
      )}

      {tip && (
        <MdTip rect={tip.rect} className="la-tipbox">
          <div className="la-tip">
            {tip.row.jobs.map((j) => (
              <div key={j.projectID}>
                <b>{j.altProjectID || `#${j.projectID}`}</b>
                <span>{j.name}</span>
                <em>{j.hours ? `${hrs(j.hours)} h` : money(j.pieceAmount)}</em>
              </div>
            ))}
          </div>
        </MdTip>
      )}

      <div className={`md-toast${toastOn ? ' on' : ''}`} role="status" aria-live="polite">{toast}</div>
    </div>
  );
};

export default LaSickPage;
